import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { of, pipe } from 'rxjs';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';
import { PartnerService } from '../services';
import { Customer, Sort } from '../models';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { SelectedPartnerStore } from './selected-partner.store';

interface PartnerCustomerState {
  customers: Customer[];
  loading: boolean;
  partnerId: string | undefined;
  sort: Sort;
  searchText: string;
  page: number;
}

const initialState: PartnerCustomerState = {
  customers: [],
  loading: false,
  partnerId: undefined,
  sort: { sortBy: null, direction: null },
  searchText: '',
  page: 0,
};

export const PartnerCustomerStore = signalStore(
  { providedIn: 'root' },
  withProps(() => {
    return { partner: inject(SelectedPartnerStore)?.selectedPartner };
  }),
  withState<PartnerCustomerState>(initialState),
  withMethods(
    (
      store,
      partnerService = inject(PartnerService),
      toast = inject(ToastrService),
      translate = inject(TranslateService),
    ) => {
      const loadPartnerCustomers = rxMethod<{ partnerId: string | undefined; sort?: Sort; searchText?: string }>(
        pipe(
          map(({ partnerId, sort, searchText }) => {
            patchState(store, {
              loading: true,
              partnerId,
              sort: sort || store.sort(),
              searchText: searchText || store.searchText(),
              page: 0,
            });
            return { partnerId, sort: sort || store.sort(), searchText: searchText || store.searchText() };
          }),
          switchMap(({ partnerId, sort, searchText }) =>
            partnerService.getPartnerCustomers({ page: 0, size: 20, sort, searchText }, partnerId).pipe(
              tap((customers) => patchState(store, { customers })),
              catchError((error) => {
                console.error('Error loading partner customers data:', error);
                toast.error(translate.instant('customers.alerts.loadFail'));
                patchState(store, { customers: [] });
                return of();
              }),
              finalize(() => patchState(store, { loading: false })),
            ),
          ),
        ),
      );

      const loadMorePartnerCustomers = rxMethod<void>(
        pipe(
          tap(() => {
            const currentPage = store.page() ?? 0;
            patchState(store, { loading: true, page: currentPage + 1 });
          }),
          switchMap(() =>
            partnerService
              .getPartnerCustomers(
                {
                  page: store.page(),
                  size: 20,
                  sort: store.sort(),
                  searchText: store.searchText(),
                },
                store.partnerId(),
              )
              .pipe(
                tap((customers) =>
                  patchState(store, {
                    customers: [...store.customers(), ...customers],
                  }),
                ),
                catchError(() => of()), // Prevent rxMethod from crashing
                finalize(() => patchState(store, { loading: false })),
              ),
          ),
        ),
      );

      return {
        loadPartnerCustomers,
        loadMorePartnerCustomers,
      };
    },
  ),
);
